import {
  PRODUCTION_FACEBOOK_APP_ID,
  DEVELOPMENT_FACEBOOK_APP_ID
} from '../../constants';
import { onProductionHostname } from '../../utils/env';
import { loadScriptInHeadImmediately } from '../../utils/dom';
import { parse as parseUrl } from 'url';
import {nth} from 'lodash';

export const extractVideoUrlFromEmbedCode = embedCode => {
  const iframeSrc = nth(
    embedCode.match(/iframe\ src=\"(\S+)\"/), // eslint-disable-line no-useless-escape
    1
  );
  if (!iframeSrc) {
    return null;
  }
  const videoUrl = parseUrl(iframeSrc, true).query.href;
  if (!videoUrl) {
    return null;
  }
  return videoUrl;
};

export const initializeFacebookPlayer = (
  playerId, containerDomNode, customEvents = {}
) =>
  fetchFB(containerDomNode)
    .then(FB => subscribeToEvents(FB, playerId, containerDomNode, customEvents));

function fetchFB(containerDomNode) {
  if (window.FB) {
    // The SDK is already loaded
    window.FB.XFBML.parse(containerDomNode);
    return Promise.resolve(window.FB);
  }

  if (window.fbAsyncInit) {
    /*
     * If window.fbAsyncInit exists but window.FB does not,
     * that means the SDK has not yet loaded, but has already
     * been requested by something else. When window.fbAsyncInit
     * is executed by the SDK, window.FB should be present.
     */
    return new Promise(resolve => {
      const oldFbAsyncInit = window.fbAsyncInit;
      window.fbAsyncInit = () => {
        oldFbAsyncInit();
        resolve(window.FB);
      };
    });
  }

  return new Promise(resolve => {
    window.fbAsyncInit = () => {
      const appId = String(
        onProductionHostname()
          ? PRODUCTION_FACEBOOK_APP_ID
          : DEVELOPMENT_FACEBOOK_APP_ID
      );

      window.FB.init({
        appId,
        status: false,
        xfbml: true,
        version: 'v3.0'
      });

      resolve(window.FB);
    };

    loadScriptInHeadImmediately('necessary', 'https://connect.facebook.net/en_US/sdk.js');
  });
}

/*
 * Custom Events
 * -------------
 * You can pass the following functions into the `customEvents` param above.
 * They are invoked when the corresponding SDK event fires, and are named the
 * same as those events:
 * - `startedPlaying`
 * - `paused`
 * - `finishedPlaying`
 *
 * In addition, you can pass in one special event:
 * - `ready`.
 * `ready` is called when the video player is actually visible. Note that this
 * happens a little bit _after_ the player's native 'xfbml.ready' event.
 */

function subscribeToEvents(FB, playerId, containerDomNode, customEvents) {
  FB.Event.subscribe(
    'xfbml.ready',
    msg => {
      if (
        msg.type === 'video'
        && msg.id === playerId
      ) {
        if (customEvents.ready) {
          setupReadyCallback(containerDomNode, customEvents.ready);
        }
        const supportedEvents = [
          'startedPlaying', 'paused', 'finishedPlaying'
        ];
        supportedEvents
          .filter(eventName => !!customEvents[eventName])
          .forEach(eventName => {
            msg.instance.subscribe(eventName, () => {
              customEvents[eventName]();
            });
          });
      }
    }
  );
}

const shouldFireReady = containerDomNode =>
  containerDomNode.querySelector('iframe')
    .style.visibility === 'visible';

function setupReadyCallback(containerDomNode, ready) {
  if (shouldFireReady(containerDomNode)) {
    ready();
  }
  const intervalID = window.setInterval(() => {
    if (shouldFireReady(containerDomNode)) {
      ready();
      window.clearInterval(intervalID);
    }
  }, 100);
}
